import React, { useCallback, useEffect, useState } from 'react';
import { procurementService } from '../../services/api';
import CascaDeModal from '../shared/CascaDeModal';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../../utils/statusColors';
import type { AgendaDiagnostico } from './agendaGoogle';

/**
 * Diagnóstico da integração com a agenda do Google: "está armada, em que modo,
 * e o que falta fazer", sem abrir log no servidor.
 *
 * ⚠️ `plano.total = 0` é o estado SAUDÁVEL: tudo o que devia estar na agenda
 * já está, e a varredura não tem o que fazer. A tela não pode dizer "nenhum
 * evento" nem parecer parada. O número que conta a história é quantos itens
 * TÊM evento (`itensComEvento`, contado da lista) ao lado do portão.
 *
 * ⛔ Não há `gmail` no diagnóstico desde 01/10/2026 (PR back #278): o HUB
 * perdeu de propósito a leitura de e-mail. Não desenhe nada de Gmail.
 */
export default function AgendaGooglePanel({
  onClose,
  onSincronizado,
  itensComEvento,
  showToast,
}: {
  onClose: () => void;
  onSincronizado: () => void;
  /** Itens da lista do Cadastrar com `googleEventId`. */
  itensComEvento: number;
  showToast: (msg: string, type?: 'success' | 'error') => void;
}) {
  const [diag, setDiag] = useState<AgendaDiagnostico | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setDiag(await procurementService.agendaDiagnostico());
    } catch (e: any) {
      setErro(e?.response?.data?.message || e?.message || 'Não foi possível ler o diagnóstico.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const r = await procurementService.agendaSincronizar();
      if (!r.armada) showToast('A agenda está desarmada: a varredura não tocou em nada.', 'error');
      else if (r.modo === 'seco') showToast(`Varredura em modo seco: ${r.plano} ação(ões) planejada(s), nada publicado.`);
      else if (r.plano === 0) showToast('Varredura feita. Nada a mudar: a agenda já estava em dia.');
      else showToast(`Varredura feita: ${r.aplicados} de ${r.plano} ação(ões) aplicada(s).`);
      onSincronizado();
      await carregar();
    } catch (e: any) {
      showToast('Erro ao sincronizar: ' + (e?.response?.data?.message || e?.message), 'error');
    } finally {
      setSincronizando(false);
    }
  };

  const plano = diag?.plano;
  const portao = diag?.portao;
  const ACAO: Record<string, string> = { criar: 'criar', atualizar: 'atualizar', cancelar: 'cancelar', nada: 'nada' };

  return (
    // Diagnóstico (só leitura + "Sincronizar agora", que grava no servidor): fecha
    // ao clicar fora, como já era.
    <CascaDeModal
      largura="base"
      fecharAoClicarFora
      titulo="Agenda do Google"
      onClose={onClose}
      corpoClassName="p-4 space-y-4"
      rodape={
        <div className="ml-auto flex gap-2">
          <button onClick={carregar} disabled={carregando} className="px-3 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-50">
            {carregando ? 'Lendo…' : 'Reler'}
          </button>
          <button
            onClick={sincronizar}
            disabled={sincronizando || !diag?.armada}
            title={diag && !diag.armada ? 'Desarmada: não há o que sincronizar' : 'Roda a varredura agora, sem esperar o cron'}
            className="px-3 py-1.5 text-xs rounded-lg bg-primary hover:bg-primary-strong text-white disabled:opacity-50"
          >
            {sincronizando ? 'Sincronizando…' : 'Sincronizar agora'}
          </button>
        </div>
      }
    >

        {carregando && !diag && <div className="text-sm text-slate-400 py-6 text-center">Lendo o diagnóstico…</div>}
        {erro && <div className={`text-sm border rounded-lg px-3 py-2 ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`}>{erro}</div>}

        {diag && (
          <>
            {/* Estado: armada, e em que modo. Seco = planeja e não publica.
                Desarmada é automação desligada, estado normal e reversível: neutral,
                não danger (§4). Falha é só o erro de leitura do diagnóstico, acima. */}
            <div className={`rounded-lg border px-3 py-2.5 text-sm ${
              !diag.armada ? `${TONE_SURFACE.neutral} ${TONE_BORDER.neutral} ${TONE_TEXT.neutral}`
                : diag.modo === 'seco' ? `${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`
                : `${TONE_SURFACE.open} ${TONE_BORDER.open} ${TONE_TEXT.open}`
            }`}>
              {!diag.armada
                ? <><strong>Desarmada.</strong> {diag.motivo || 'Sem credencial configurada.'} Nenhum evento é criado nem cancelado.</>
                : diag.modo === 'seco'
                  ? <><strong>Armada, em modo seco.</strong> A varredura planeja, mas não publica nada. Marcas manuais ficam gravadas e viram evento quando o modo passar a ativo.</>
                  : <><strong>Armada, em modo ativo.</strong> A varredura publica, atualiza e cancela eventos.</>}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-central-elevado/60 border border-central-borda">
                <div className="text-[11px] text-slate-400">Com evento na agenda</div>
                <div className="text-2xl font-bold text-emerald-400">{itensComEvento}</div>
                <div className="text-[10px] text-slate-500">nesta lista</div>
              </div>
              <div className="p-3 rounded-lg bg-central-elevado/60 border border-central-borda">
                <div className="text-[11px] text-slate-400">Com proposta enviada</div>
                <div className="text-2xl font-bold text-slate-200">{portao?.comPropostaEnviada ?? '—'}</div>
                <div className="text-[10px] text-slate-500">entram pelo portão</div>
              </div>
              <div className="p-3 rounded-lg bg-central-elevado/60 border border-central-borda">
                <div className="text-[11px] text-slate-400">Sessões na janela</div>
                <div className="text-2xl font-bold text-slate-200">{portao?.candidatosNaJanela ?? '—'}</div>
                <div className="text-[10px] text-slate-500">candidatas</div>
              </div>
              <div className="p-3 rounded-lg bg-central-elevado/60 border border-central-borda">
                <div className="text-[11px] text-slate-400">Pendências da varredura</div>
                <div className={`text-2xl font-bold ${plano && plano.total > 0 ? 'text-amber-400' : 'text-slate-200'}`}>{plano?.total ?? '—'}</div>
                <div className="text-[10px] text-slate-500">{plano && plano.total === 0 ? 'agenda em dia' : 'a aplicar'}</div>
              </div>
            </div>

            {plano && plano.total === 0 && diag.armada && (
              <p className="text-xs text-slate-400">
                Nada pendente: o que deve estar na agenda já está. Isso é o estado normal, não falta de dados.
              </p>
            )}

            {plano && plano.total > 0 && (
              <div>
                <div className="text-xs text-slate-400 mb-1.5">
                  Próximas ações ({Object.entries(plano.porAcao).map(([k, v]) => `${v} ${ACAO[k] || k}`).join(', ')})
                </div>
                <ul className="text-xs divide-y divide-slate-800 border border-central-borda rounded-lg">
                  {plano.primeiros.map((a) => (
                    <li key={`${a.itemId}-${a.acao}`} className="px-3 py-1.5 flex gap-2">
                      <span className="text-slate-500 w-16 shrink-0">{ACAO[a.acao] || a.acao}</span>
                      <span className="text-slate-300 min-w-0 truncate" title={a.titulo || ''}>#{a.itemId} {a.titulo || ''}</span>
                      {a.motivo && <span className="text-slate-500 ml-auto shrink-0">{a.motivo}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {diag.avisos && diag.avisos.length > 0 && (
              <ul className="text-xs text-amber-200 bg-amber-500/10 border border-amber-600/40 rounded-lg px-3 py-2 space-y-1 list-disc list-inside">
                {diag.avisos.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            )}

            {portao?.regra && (
              <div className="text-[11px] text-slate-500">
                Regra do portão: <code className="text-slate-400">{portao.regra}</code>. Marcar ou tirar à mão sobrepõe essa regra item a item.
              </div>
            )}

            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-[11px]">
              <dt className="text-slate-500">Conta de serviço</dt><dd className="text-slate-300 font-mono truncate">{diag.contaDeServico || '—'}</dd>
              <dt className="text-slate-500">Em nome de</dt><dd className="text-slate-300 font-mono truncate">{diag.impersonando || '—'}</dd>
              <dt className="text-slate-500">Agenda</dt><dd className="text-slate-300 font-mono truncate">{diag.calendarId || '—'}</dd>
              <dt className="text-slate-500">Escopos</dt><dd className="text-slate-300 font-mono break-all">{(diag.escopos || []).join(', ') || '—'}</dd>
            </dl>
          </>
        )}

    </CascaDeModal>
  );
}
